import { GraphQLRequest } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import jwt_decode from 'jwt-decode';
import { isInvalidGrant, safeStringify } from '@oolio-group/client-utils';
import { App } from '@oolio-group/domain';
import * as settings from '../state/preferences';
import { TokenRefreshLink } from 'apollo-link-token-refresh';
import { TokenResponse } from '@openid/appauth';
import { tokenState } from '../state/apolloVars';
import { noopHandler } from './errorHandlers';
import { refresh } from './refetchTokenSerially';
import {
  POSTHOG_EVENT_ACTIONS,
  POSTHOG_EVENT_DATA,
  analyticsService,
} from '../analytics/AnalyticsService';
import Sentry from './Sentry/Sentry';

export { isInvalidGrant, safeStringify };

interface AuthHeader {
  authorization?: string;
  organization?: string;
  venue?: string;
  store?: string;
  device?: string;
  appToken?: string;
  kitchenDisplay?: string;
  app?: App;
}

export const tokenLookup = async (): Promise<AuthHeader> => {
  const session = await settings.getSession();
  return {
    organization: session?.currentOrganization?.id || '',
    venue: session?.currentVenue?.id || '',
    store: session?.currentStore?.id || '',
    authorization: session?.token || '',
    device: session?.device?.id || '',
    kitchenDisplay: session?.kitchenDisplay?.id || '',
    app: session?.activeApp,
  };
};

const isLoginMutation = (request: GraphQLRequest) =>
  request.operationName === 'kdsLoginByDeviceCode';

/* istanbul ignore file */
export const withToken = setContext((request, { headers }) => {
  return isLoginMutation(request)
    ? headers
    : tokenLookup().then(tokenHeaders => ({
        headers: {
          ...tokenHeaders,
          ...headers,
        },
      }));
});

/**
 * This will only validate or handle refresh token related errors
 */
export const refreshSession = new TokenRefreshLink({
  isTokenValidOrUndefined: async () => {
    const session = await settings.getSession();
    if (
      session?.token &&
      session?.expiredDate &&
      (session?.expiredDate as number) > Date.now()
    ) {
      return true;
    }
    return false;
  },
  // Shares the same single-flighted refresher as refetchTokenSerially.ts's
  // other callers (docket poll, docket bump), so a GraphQL-triggered refresh
  // can't race them into Auth0's rotation reuse detection.
  fetchAccessToken: () => refresh(),
  handleResponse: () => async (response: Response) => {
    const { accessToken, refreshToken } = response as unknown as TokenResponse;
    const oldSession = await settings.getSession();

    const accessTokenPayload = jwt_decode<{
      exp: number;
    }>(accessToken as string);

    // update new access token
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const newTokenPayload: any = {
      token: accessToken as string,
      refreshToken: refreshToken as string,
      expiredDate: accessTokenPayload.exp * 1000,
    };
    tokenState(newTokenPayload);
    await settings.setSession({
      ...oldSession,
      ...newTokenPayload,
    });
    const session = await settings.getSession();
    analyticsService.capture(POSTHOG_EVENT_ACTIONS.TOKEN_REFRESH_OR_EXPIRED, {
      orgId: session?.currentOrganization?.id,
      orgName: session?.currentOrganization?.name,
    } as POSTHOG_EVENT_DATA);
    return {
      access_token: accessToken,
    };
  },
  handleFetch: noopHandler,
  handleError: async error => {
    console.error('FAIL TO REFRESH USER TOKEN', error);
    const isDead = isInvalidGrant(error);
    Sentry.captureMessage('Token refresh failed', {
      tags: {
        stage: 'refreshSession-handleError',
        errorType: isDead ? 'invalid_grant' : 'other',
      },
      extra: {
        error:
          error instanceof Error
            ? error.message
            : error && typeof error === 'object'
            ? safeStringify(error)
            : String(error),
      },
    });
    if (isDead) {
      tokenState({});
      await clearSession();
    }
  },
});

export const clearSession = async () => {
  await settings.setSession({ authorized: false });
};

export const authFlowLink = [refreshSession, withToken];
